import {
  Bot,
  Cpu,
  Shuffle,
  Building2,
  type LucideIcon,
  LayoutDashboard,
  ListTodo,
  Radar,
  TrendingUp,
  GitMerge,
  FileText,
  Database,
  Network,
  HelpCircle,
  Send,
  Users,
  KeyRound,
  Settings,
  Sparkles,
  AlertOctagon,
  DollarSign,
  Palette,
  ShieldCheck,
  CalendarDays,
  Clapperboard,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api.js";
import type { Route } from "../lib/useRoute.js";

/**
 * Persistent left sidebar with three groups:
 *   - WORKSPACE: at-a-glance pages (Overview, Tasks)
 *   - CONTENT  : the production flow (Pipeline → Articles → Content DB → Distribution)
 *   - RESEARCH : deciding what to write + the review queues behind it
 *   - SYSTEM   : configuration (Credentials, Settings)
 *
 * Width is fixed at 240px (lg:w-60 = 240px). The main content shifts right.
 * On mobile widths we hide the sidebar entirely (TODO: hamburger drawer).
 */

interface NavItem {
  id: Route;
  label: string;
  icon: LucideIcon;
  badge?: number;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const GROUPS: NavGroup[] = [
  {
    title: "Workspace",
    items: [
      { id: "overview", label: "Overview", icon: LayoutDashboard },
      { id: "tasks", label: "Tasks", icon: ListTodo },
    ],
  },
  {
    // The production flow, in the order a piece actually moves through it.
    // Everything here is touched most days.
    title: "Content",
    items: [
      { id: "pipeline", label: "Pipeline", icon: GitMerge },
      { id: "content", label: "Articles", icon: FileText },
      { id: "content-db", label: "Content DB", icon: Database },
      { id: "calendar", label: "Calendar", icon: CalendarDays },
      { id: "video", label: "Video", icon: Clapperboard },
      { id: "distribution", label: "Distribution", icon: Send },
      { id: "audience", label: "Audience", icon: Users },
    ],
  },
  {
    // Deciding WHAT to write, and the review queues behind it. Visited when
    // planning rather than when producing — which is why these were crowding
    // out the daily screens while sharing one group with them.
    title: "Research",
    items: [
      { id: "research", label: "Keyword Research", icon: TrendingUp },
      { id: "sources", label: "Sources", icon: Radar },
      { id: "entities", label: "Entities", icon: Network },
      // Occasional review surfaces — real work, but not a daily stop.
      { id: "folded", label: "Folded Variants", icon: Shuffle },
      { id: "offerings", label: "Fund Offerings", icon: Building2 },
    ],
  },
  {
    title: "System",
    items: [
      { id: "automations", label: "Automations", icon: Bot },
      { id: "models", label: "Models", icon: Cpu },
      { id: "credentials", label: "Credentials", icon: KeyRound },
      { id: "brand", label: "Brand kit", icon: Palette },
      { id: "errors", label: "Errors", icon: AlertOctagon },
      { id: "spend", label: "Spend", icon: DollarSign },
      { id: "audit", label: "Site Audit", icon: ShieldCheck },
      { id: "help", label: "Help", icon: HelpCircle },
    ],
  },
];

export function Sidebar({
  route,
  onRoute,
  onOpenChat,
}: {
  route: Route;
  onRoute: (r: Route) => void;
  onOpenChat: () => void;
}) {
  // Live unresolved-error count drives the red badge on the Errors entry.
  // 30s polling matches the Errors page itself.
  const errorCountsQuery = useQuery({
    queryKey: ["errors", "counts", "sidebar"],
    queryFn: () => api.errorCounts(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const unresolvedErrors = errorCountsQuery.data?.unresolved.error ?? 0;

  // Inject the badge into the System group's Errors item.
  const groups = GROUPS.map((group) => {
    if (group.title !== "System") return group;
    return {
      ...group,
      items: group.items.map((item) =>
        item.id === "errors" && unresolvedErrors > 0
          ? { ...item, badge: unresolvedErrors }
          : item,
      ),
    };
  });

  return (
    <aside className="hidden lg:flex lg:sticky lg:top-0 lg:h-screen lg:w-60 lg:shrink-0 lg:flex-col lg:self-start lg:border-r lg:border-line lg:bg-white">
      {/* Logo block */}
      <div className="flex h-16 items-center gap-3 border-b border-line px-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-brand-600 to-brand-800 text-base font-bold text-white shadow-sm">
          A
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-ink">AlternaBe</div>
          <div className="truncate text-2xs text-muted">Operator workspace</div>
        </div>
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 scrollbar-thin">
        {groups.map((group) => (
          <div key={group.title} className="mb-5">
            <div className="mb-1.5 px-2 text-2xs font-semibold uppercase tracking-wider text-muted/70">
              {group.title}
            </div>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = route === item.id;
                const Icon = item.icon;
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => onRoute(item.id)}
                      className={
                        active
                          ? "flex w-full items-center justify-between gap-2 rounded-lg bg-brand-50 px-2.5 py-2 text-sm font-medium text-brand-800 ring-1 ring-inset ring-brand/10"
                          : "flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-sm font-medium text-muted-dark transition hover:bg-line/60 hover:text-ink"
                      }
                    >
                      <span className="flex items-center gap-2.5">
                        <Icon className={active ? "h-4 w-4 text-brand-700" : "h-4 w-4 text-muted"} strokeWidth={2} />
                        {item.label}
                      </span>
                      {item.badge !== undefined && item.badge > 0 && (
                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-white ${item.id === "errors" ? "bg-danger" : "bg-warn"}`}>
                          {item.badge}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer: chat trigger + version */}
      <div className="border-t border-line px-3 py-3 space-y-2">
        <button
          onClick={onOpenChat}
          className="group flex w-full items-center gap-2.5 rounded-lg bg-gradient-to-r from-brand-700 to-brand-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition hover:shadow-card-hover"
        >
          <Sparkles className="h-4 w-4" strokeWidth={2.2} />
          <span>Ask AlternaBe AI</span>
          <kbd className="ml-auto rounded bg-white/20 px-1.5 py-0.5 text-2xs font-mono text-white/90">⌘K</kbd>
        </button>
        <div className="flex items-center justify-between px-2 text-2xs text-muted/70">
          <span>v0.2 · build {new Date().toISOString().slice(0, 10)}</span>
          <Settings className="h-3.5 w-3.5 cursor-pointer hover:text-ink" />
        </div>
      </div>
    </aside>
  );
}
